import { useRef, useState, useEffect, useCallback } from "react";
import { Camera, RefreshCw, Upload, X, Check } from "lucide-react";
import { Button } from "@/components/ui/button";

export function SelfieCapture({ onCapture, capturedImage, onClear }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileRef = useRef(null);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState("");
  const streamRef = useRef(null);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setStreaming(false);
  }, []);

  const startCamera = useCallback(async () => {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: 640, height: 480 },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setStreaming(true);
    } catch (e) {
      setError("Camera unavailable. Use photo upload instead.");
    }
  }, []);

  useEffect(() => () => stopStream(), [stopStream]);

  const shoot = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext("2d");
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
    stopStream();
    onCapture(dataUrl);
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => onCapture(reader.result);
    reader.readAsDataURL(file);
  };

  if (capturedImage) {
    return (
      <div className="space-y-3" data-testid="selfie-preview-block">
        <div className="relative overflow-hidden rounded-2xl border-2 border-emerald-400 aspect-video bg-slate-950">
          <img src={capturedImage} alt="selfie" className="w-full h-full object-cover" data-testid="selfie-preview-img" />
          <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-emerald-500 text-white text-xs font-semibold px-2.5 py-1 rounded-full">
            <Check className="h-3.5 w-3.5" /> Captured
          </div>
        </div>
        <Button variant="outline" onClick={onClear} className="w-full" data-testid="selfie-retake-btn">
          <RefreshCw className="h-4 w-4" /> Retake photo
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="selfie-capture-block">
      <div className="relative overflow-hidden rounded-2xl border-2 border-dashed border-indigo-400/50 aspect-video bg-slate-950 flex items-center justify-center">
        <video ref={videoRef} className="w-full h-full object-cover -scale-x-100" playsInline muted style={{ display: streaming ? "block" : "none" }} />
        {!streaming && (
          <div className="text-center text-slate-400 px-6">
            <Camera className="h-10 w-10 mx-auto mb-2 opacity-60" />
            <p className="text-sm">{error || "Camera preview will appear here"}</p>
          </div>
        )}
        <canvas ref={canvasRef} className="hidden" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        {!streaming ? (
          <Button onClick={startCamera} className="col-span-1" data-testid="selfie-start-camera-btn">
            <Camera className="h-4 w-4" /> Start camera
          </Button>
        ) : (
          <Button onClick={shoot} className="col-span-1 bg-emerald-600 hover:bg-emerald-700" data-testid="selfie-shoot-btn">
            <Camera className="h-4 w-4" /> Take photo
          </Button>
        )}
        <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="selfie-upload-btn">
          <Upload className="h-4 w-4" /> Upload
        </Button>
        <input ref={fileRef} type="file" accept="image/*" capture="user" className="hidden" onChange={onFile} data-testid="selfie-file-input" />
      </div>
      {streaming && (
        <Button variant="ghost" size="sm" onClick={stopStream} className="w-full text-slate-500" data-testid="selfie-stop-btn">
          <X className="h-4 w-4" /> Cancel
        </Button>
      )}
    </div>
  );
}
